import { useState, useCallback, useEffect } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import {
  Controller,
  type Control,
  type ControllerRenderProps,
  type UseFormWatch
} from 'react-hook-form'
import { Button, Field, FieldHelp, FieldLabel, Input, Progress } from '@axilon-apps/libs-ui-shadcn'
import calculatePasswordComplexity from '../../utils/password/calculate-password-complexity'
import { getInitialRequirements } from '../../utils/password/calculate-password-strength'
import PasswordRequirementsTooltip from './components/PasswordRequirementsTooltip/PasswordRequirementsTooltip'
import type { Maybe } from '@axilon-apps/libs-shared-types'

import type { PasswordRequirement } from '../../types/password'

import { DataTestId } from '../../constants/data-test-id'

interface Props {
  name: string
  label: string
  control: Control<any>
  watch: UseFormWatch<any>
  error: Maybe<string | string[]>
  showComplexity?: boolean
  disabled?: boolean
}

const PasswordInput = ({
  name,
  label,
  control,
  watch,
  error,
  showComplexity = true,
  disabled = false
}: Props) => {
  const [strength, setStrength] = useState(0)
  const [feedback, setFeedback] = useState('')
  const [requirements, setRequirements] = useState<PasswordRequirement[]>(getInitialRequirements())
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (!name) {
        setStrength(0)
        setFeedback('')
        setRequirements(getInitialRequirements())
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const passwordChange = useCallback(
    (field: ControllerRenderProps) =>
      ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        const { feedback, strength, requirements } = calculatePasswordComplexity(value)

        setStrength(strength)
        setFeedback(feedback)
        setRequirements(requirements)
        field.onChange(value)
      },
    []
  )

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev)
  }, [])

  // Hue derived from strength so the progress bar + label go red → green.
  const hue = Math.min(strength * 1.2, 120)
  const accentStyle = { ['--hue' as string]: `${hue}` } as React.CSSProperties

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        // `data-invalid` flips the wrapping Field into the destructive
        // state — Input border + FieldHelp text both go red via the
        // `group-data-[invalid=true]/field:…` cascade. Without it, an
        // empty password (or any validation error) showed a muted
        // helper-text and no border-color change, while sibling
        // <Field>'d inputs (e.g. Username) lit up red correctly.
        <Field data-invalid={Boolean(error) || undefined}>
          <div className="flex flex-row items-baseline gap-2">
            <FieldLabel>{label}</FieldLabel>
            {showComplexity && <PasswordRequirementsTooltip requirements={requirements} />}
          </div>
          <div className="flex flex-col gap-1" style={accentStyle}>
            <div className="relative">
              <Input
                {...field}
                onChange={passwordChange(field)}
                type={showPassword ? 'text' : 'password'}
                disabled={disabled}
                data-testid={DataTestId.PasswordInput}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={toggleShowPassword}
                className="absolute top-1/2 right-1 -translate-y-1/2"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </Button>
            </div>
            {showComplexity && (
              <>
                <Progress value={strength} className="h-1.5 [&>div]:bg-[hsl(var(--hue)_80%_40%)]" />
                <p
                  className="self-end text-xs text-[hsl(var(--hue)_80%_30%)]"
                  data-testid={DataTestId.PasswordFeedbackText}
                >
                  {feedback}
                </p>
              </>
            )}
          </div>
          {/* FieldHelp auto-flips to text-destructive when its wrapping
              Field has `data-invalid="true"`, so error messages match
              the red Input border in the same render. */}
          <FieldHelp>{Array.isArray(error) ? error.join(', ') : error}</FieldHelp>
        </Field>
      )}
    />
  )
}

export default PasswordInput
